import axiosInstance from '../axiosConfig';
import {
  NonMemberFamily,
  NonMemberFamilyFormData,
  NonMemberFamilyResponse,
  NonMemberFamiliesResponse,
} from '../../types';

/**
 * Non-Member Family Service
 * Handles all non-member family member related API calls
 */
class NonMemberFamilyService {
  private baseUrl = '/non-members';

  /**
   * Get all family members for a non-member
   */
  async getByNonMember(nonMemberId: string): Promise<NonMemberFamiliesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${nonMemberId}/family`);
    return response.data;
  }

  /**
   * Get a specific family member
   */
  async getById(nonMemberId: string, familyId: number): Promise<NonMemberFamilyResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${nonMemberId}/family/${familyId}`);
    return response.data;
  }

  /**
   * Add a family member to a non-member account
   */
  async create(
    nonMemberId: string,
    data: NonMemberFamilyFormData
  ): Promise<NonMemberFamilyResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${nonMemberId}/family`, data);
    return response.data;
  }

  /**
   * Update a family member
   */
  async update(
    nonMemberId: string,
    familyId: number,
    data: Partial<NonMemberFamilyFormData>
  ): Promise<NonMemberFamilyResponse> {
    const response = await axiosInstance.put(`${this.baseUrl}/${nonMemberId}/family/${familyId}`, data);
    return response.data;
  }

  /**
   * Soft delete (deactivate) a family member
   */
  async delete(nonMemberId: string, familyId: number): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete(`${this.baseUrl}/${nonMemberId}/family/${familyId}`);
    return response.data;
  }

  /**
   * Get kids club eligible children for a non-member
   */
  async getKidsClubEligible(nonMemberId: string): Promise<NonMemberFamiliesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${nonMemberId}/family/kids-club-eligible`);
    return response.data;
  }

  /**
   * Get lessons eligible family members for a non-member
   */
  async getLessonsEligible(nonMemberId: string): Promise<NonMemberFamiliesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${nonMemberId}/family/lessons-eligible`);
    return response.data;
  }

  /**
   * Update kids club consent
   */
  async updateKidsClubConsent(
    nonMemberId: string,
    familyId: number,
    data: {
      photo_consent?: boolean;
      allergies?: string;
      medical_conditions?: string;
      dietary_restrictions?: string;
      emergency_contact_name?: string;
      emergency_contact_phone?: string;
      authorized_pickup_persons?: { name: string; phone: string; relationship: string }[];
    }
  ): Promise<NonMemberFamilyResponse> {
    const response = await axiosInstance.put(
      `${this.baseUrl}/${nonMemberId}/family/${familyId}/kids-club-consent`,
      data
    );
    return response.data;
  }
}

// Export singleton instance
export const nonMemberFamilyService = new NonMemberFamilyService();
export default nonMemberFamilyService;
